import React, { memo, useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import { GridToolbarContainer } from '@mui/x-data-grid-pro';
import { CustomToolbarExtendedProps, MultiSelectButtonDef } from '../datagrid-types';
import CustomSearch from './CustomSearch';
import { Fragment, useState } from 'react';
import ImportedRowsPopup from './ImportedRowsPopup';
import { ExportVariant, FilterVariant, Printer } from 'mdi-material-ui';
import { GridCsvExportOptions, useGridApiContext } from '@mui/x-data-grid-premium';
import { VigoTooltip } from '../../vigo-tooltip';
import { checkRowsForErrors } from '../helpers';

export interface ImportViewDto {
  pagedItems?: any[];
  allItems?: any[];
  totalItemCount?: number;
}

const CustomToolbar = ({
  masterDataObject,
  lookupColumns,
  disableSearch,
  multiSelect,
  showMultiSelectButtons,
  multiSelectButtons,
  headerButtonGroup,
  disableExport,
  disableFilters,
  disablePrint,
  refresh,
  editServiceEndpoint,
  editServiceLocation,
  selectedTableId,
  fetchPageSize,
  filterColumn,
  setLoading,
  setGridRows,
  setMasterDataRows,
  sort,
  columns,
  setCustomType,
  usePagination,
  searchOverride,
  customPlaceholder,
  onSearchChange,
  initialSearchValue,
  ref
}: CustomToolbarExtendedProps) => {
  // State
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [fileTooBig, setFileTooBig] = useState<boolean>(false);
  const [importedRowHeaders, setImportedRowHeaders] = useState<any[]>([]);
  const [valuesToImport, setValuesToImport] = useState<ImportViewDto>({});
  const [errorMappingFile, setErrorMappingFile] = useState<boolean>(false);

  // Refs
  const apiRef = useGridApiContext();

  const handleExport = useCallback(() => {
    const options: GridCsvExportOptions = {
      fileName:
        masterDataObject?.masterDataTable?.description != undefined
          ? masterDataObject?.masterDataTable?.description
          : 'export'
    };

    apiRef.current.exportDataAsCsv(options);
  }, [apiRef, masterDataObject?.masterDataTable?.description]);

  const handleFilter = useCallback(() => {
    apiRef.current.showFilterPanel();
  }, [apiRef]);

  const handlePrint = useCallback(() => {
    const visibleColumns = apiRef.current
      .getAllColumns()
      .filter(col => col.field !== 'actions' && col.field !== '__check__');
    const columnCount = visibleColumns.length;

    // Use landscape if more than 6 columns
    const useLandscape = columnCount > 6;

    apiRef.current.exportDataAsPrint({
      hideFooter: true,
      hideToolbar: true,
      pageStyle: useLandscape
        ? `@page { size: A4 landscape; margin: 10mm; }`
        : `@page { size: A4 portrait; margin: 10mm; }`
    });
  }, [apiRef]);

  const buttonShouldShow = (button: MultiSelectButtonDef) => {
    if (button.alwaysShow) {
      return true;
    }

    if (multiSelect) {
      if (button.showWithNoSelection) {
        return true;
      }
    }

    return showMultiSelectButtons;
  };

  return (
    <>
      <GridToolbarContainer
        sx={{ pt: 3, pb: 3, display: 'flex', alignItems: 'stretch', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: '15px' }}>
          {!disableSearch && (
            <CustomSearch
              {...{
                setGridRows,
                editServiceEndpoint,
                editServiceLocation,
                selectedTableId,
                filterColumn,
                columns,
                setLoading,
                sort,
                setMasterDataRows,
                fetchPageSize,
                setCustomType,
                masterDataObject,
                usePagination,
                searchOverride,
                customPlaceholder,
                onSearchChange,
                initialSearchValue,
                ref
              }}
            />
          )}
          {/* {!disableFilters && (
            <GridToolbarFilterButton nonce={undefined} onResize={undefined} onResizeCapture={undefined} />
          )} */}
          {multiSelectButtons
            ?.filter(button => buttonShouldShow(button))
            ?.map(button => (
              <Fragment key={button.name}>{button.component}</Fragment>
            ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'flex-end',
            flexDirection: { xs: 'column', md: 'row' },
            marginTop: '-10px'
          }}>
          <Box sx={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
            {headerButtonGroup &&
              headerButtonGroup.map((item, index) => {
                // eslint-disable-next-line react/no-array-index-key
                return <Fragment key={index}>{item}</Fragment>;
              })}

            {!disableExport && (
              <VigoTooltip title={'Export to CSV'}>
                <IconButton
                  sx={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 2, paddingRight: 2 }}
                  size="large"
                  component="label"
                  onClick={handleExport}>
                  <ExportVariant />
                </IconButton>
              </VigoTooltip>
            )}
            {!disablePrint && (
              <VigoTooltip title={'Print'}>
                <IconButton
                  sx={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 2, paddingRight: 2 }}
                  size="large"
                  component="label"
                  onClick={handlePrint}>
                  <Printer />
                </IconButton>
              </VigoTooltip>
            )}
            {!disableFilters && (
              <VigoTooltip title={'Filter'}>
                <IconButton
                  sx={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 2, paddingRight: 2 }}
                  size="large"
                  component="label"
                  onClick={handleFilter}>
                  <FilterVariant />
                </IconButton>
              </VigoTooltip>
            )}
          </Box>
        </Box>
      </GridToolbarContainer>

      {(showPopup || fileTooBig) && (
        <ImportedRowsPopup
          open={showPopup}
          popupDisplayHeaders={importedRowHeaders}
          values={valuesToImport}
          setValues={setValuesToImport}
          setOpen={setShowPopup}
          setRowHeaders={setImportedRowHeaders}
          endPoint={editServiceEndpoint}
          location={editServiceLocation}
          {...{
            refresh,
            lookupColumns,
            masterDataObject,
            checkRowsForErrors,
            errorMappingFile,
            setErrorMappingFile,
            fileTooBig,
            setFileTooBig
          }}
        />
      )}
    </>
  );
};

export default memo(CustomToolbar);
